import { useState } from 'react';
import Button from '../../../components/base/Button';

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  image: string;
}

export default function EventsSection() {
  const [clickCount, setClickCount] = useState(0);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    date: '',
    image: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [imagePreview, setImagePreview] = useState('');

  const handleBadgeClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    if (newCount === 5) {
      setShowPasswordModal(true);
      setClickCount(0);
    }
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'hfc@2025') {
      setIsAuthenticated(true);
      setShowPasswordModal(false);
      setShowAdminPanel(true);
      setPassword('');
      setPasswordError('');
    } else {
      setPasswordError('Incorrect password');
      setPassword('');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setShowAdminPanel(false);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setNewEvent({ ...newEvent, image: base64String });
        setImagePreview(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAddEvent = (e: React.FormEvent) => {
    e.preventDefault();
    if (newEvent.title && newEvent.description && newEvent.date && newEvent.image) {
      const event: Event = {
        id: Date.now().toString(),
        ...newEvent
      };
      setEvents([...events, event]);
      setNewEvent({ title: '', description: '', date: '', image: '' });
      setImagePreview('');
    }
  };

  const handleDeleteEvent = (id: string) => {
    setEvents(events.filter(event => event.id !== id));
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div 
              onClick={handleBadgeClick}
              className="inline-block px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-bold uppercase tracking-wider cursor-pointer hover:bg-red-200 transition-colors"
            >
              UPCOMING EVENTS
            </div>
            {isAuthenticated && (
              <Button
                onClick={handleLogout}
                variant="secondary"
                icon="ri-logout-box-line"
                iconPosition="left"
                className="px-4 py-2 text-sm"
              >
                Exit Admin
              </Button>
            )}
          </div>
          <h3 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">
            Stay <span className="text-red-600">Updated</span>
          </h3>
        </div>

        {/* Admin Panel */}
        {isAuthenticated && showAdminPanel && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-2 border-red-600">
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-2xl font-bold text-gray-900">Admin Panel - Add Event</h4>
              <button
                onClick={() => setShowAdminPanel(false)}
                className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl text-gray-700"></i>
              </button>
            </div>
            
            <form onSubmit={handleAddEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Event Title</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm"
                  placeholder="Enter event title"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm"
                  placeholder="Enter event description"
                  rows={4}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm cursor-pointer"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Event Image</label>
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent text-sm cursor-pointer file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-50 file:text-red-700 hover:file:bg-red-100 file:cursor-pointer"
                    required
                  />
                  {imagePreview && (
                    <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-gray-200">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="w-full h-full object-cover object-center"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview('');
                          setNewEvent({ ...newEvent, image: '' });
                        }}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line text-white text-lg"></i>
                      </button>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">Maximum file size: 5MB. Supported formats: JPG, PNG, GIF, WebP</p>
                </div>
              </div>
              
              <Button
                type="submit"
                variant="submit"
                icon="ri-add-line"
                iconPosition="left"
                className="w-full px-6 py-3"
              >
                Add Event
              </Button>
            </form>
          </div>
        )}

        {/* Events Display */}
        {events.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <div key={event.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative w-full h-64">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-red-600 text-sm font-semibold mb-3">
                    <i className="ri-calendar-line"></i>
                    <span>{new Date(event.date).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</span>
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{event.description}</p>
                  
                  {isAuthenticated && (
                    <Button
                      onClick={() => handleDeleteEvent(event.id)}
                      variant="outline"
                      icon="ri-delete-bin-line"
                      iconPosition="left"
                      className="mt-4 w-full px-4 py-2 text-sm"
                    >
                      Delete Event
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-calendar-line text-4xl text-gray-400"></i>
            </div>
            <p className="text-xl text-gray-600 font-semibold">
              No upcoming events
            </p>
            <p className="text-gray-500 mt-2">
              Check back soon for exciting fitness events and challenges
            </p>
          </div>
        )}

        {/* Admin Button */}
        {isAuthenticated && !showAdminPanel && (
          <div className="text-center mt-8">
            <Button
              onClick={() => setShowAdminPanel(true)}
              variant="primary"
              icon="ri-settings-3-line"
              iconPosition="left"
              className="px-6 py-3"
            >
              Open Admin Panel
            </Button>
          </div>
        )}
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lock-line text-3xl text-red-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Admin Access</h3>
              <p className="text-gray-600 text-sm">Enter password to continue</p>
            </div>
            
            <form onSubmit={handlePasswordSubmit}>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError('');
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent mb-4 text-sm"
                placeholder="Enter password"
                autoFocus
              />
              
              {passwordError && (
                <p className="text-red-600 text-sm mb-4 text-center font-semibold">{passwordError}</p>
              )}
              
              <div className="flex gap-3">
                <Button
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPassword('');
                    setPasswordError('');
                    setClickCount(0);
                  }}
                  variant="secondary"
                  className="flex-1 px-4 py-3"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="submit"
                  icon="ri-lock-unlock-line"
                  iconPosition="left"
                  className="flex-1 px-4 py-3"
                >
                  Submit
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
